import { PrivacyBlur } from "#components/privacy-blur"
import { Badge } from "#components/ui/badge"
import { Button } from "#components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "#components/ui/tooltip"
import { formatDate } from "#lib/formatters"
import type { ExpenseCategory } from "@home-finance-manager/shared/types/expense_categories"
import type { Expense } from "@home-finance-manager/shared/types/expenses"
import type { ColumnDef } from "@tanstack/react-table"
import { Calendar as CalendarIcon, Pencil, Trash2 } from "lucide-react"

// Helper function to get category name
const getCategoryName = (
  categories: ExpenseCategory[],
  categoryId: number,
  t: (key: string) => string
): string => {
  const category = categories.find((c) => c.id === categoryId)
  return category?.name || t("components.expenses.table.unknown")
}

// Helper function to get category color
const getCategoryColor = (categories: ExpenseCategory[], categoryId: number): string => {
  const category = categories.find((c) => c.id === categoryId)
  return category?.color || "#3B82F6"
}

// Helper function to get recurrence label
const getRecurrenceLabel = (recurring: string | null, t: (key: string) => string): string => {
  if (!recurring) return t("components.expenses.table.oneTime")
  return recurring.charAt(0).toUpperCase() + recurring.slice(1)
}

export interface ExpenseTableColumnsProps {
  categories: ExpenseCategory[]
  onEdit: (expense: Expense) => void
  onDelete: (expense: Expense) => void
  isOwner: boolean
  t: (key: string) => string
  formatCurrency: (amount: number | string | null | undefined) => string
}

export const createExpenseTableColumns = ({
  categories,
  onEdit,
  onDelete,
  isOwner,
  t,
  formatCurrency,
}: ExpenseTableColumnsProps): ColumnDef<Expense>[] => {
  return [
    {
      accessorKey: "name",
      header: t("components.expenses.table.name"),
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.name}</div>
          {row.original.description && (
            <div className="text-sm text-muted-foreground">{row.original.description}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: "amount",
      header: t("components.expenses.table.amount"),
      cell: ({ row }) => (
        <div className="font-semibold">
          <PrivacyBlur>{formatCurrency(row.original.amount)}</PrivacyBlur>
        </div>
      ),
    },
    {
      accessorKey: "expenseCategoryId",
      header: t("components.expenses.table.category"),
      cell: ({ row }) => {
        const color = getCategoryColor(categories, row.original.expenseCategoryId)
        return (
          <Badge
            style={{
              backgroundColor: `${color}20`,
              color: color,
              borderColor: color,
            }}
          >
            {getCategoryName(categories, row.original.expenseCategoryId, t)}
          </Badge>
        )
      },
    },
    {
      accessorKey: "recurring",
      header: t("components.expenses.table.recurrence"),
      cell: ({ row }) => (
        <div className="text-sm">{getRecurrenceLabel(row.original.recurring, t)}</div>
      ),
    },
    {
      accessorKey: "nextPaymentDate",
      header: "",
      cell: ({ row }) => (
        <div className="flex items-center gap-2 text-sm">
          <CalendarIcon className="w-4 h-4" />
          {row.original.nextPaymentDate
            ? formatDate(row.original.nextPaymentDate)
            : t("components.expenses.table.notAvailable")}
        </div>
      ),
    },
    {
      accessorKey: "isVirtual",
      header: t("components.expenses.table.options"),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {row.original.isIncome && (
            <Badge
              style={{
                backgroundColor: "#16a34a20",
                color: "#16a34a",
                borderColor: "#16a34a",
              }}
            >
              {t("components.expenses.table.income")}
            </Badge>
          )}
          {row.original.isVirtual && (
            <Badge variant="secondary">{t("components.expenses.table.virtual")}</Badge>
          )}
          {row.original.disabledAt && (
            <Badge variant="destructive">{t("components.expenses.table.disabled")}</Badge>
          )}
        </div>
      ),
    },
    {
      id: "actions",
      header: t("components.expenses.table.actions"),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {isOwner ? (
            <>
              <Button variant="outline" size="icon" onClick={() => onEdit(row.original)}>
                <Pencil className="w-4 h-4" />
              </Button>
              <Button variant="destructive" size="icon" onClick={() => onDelete(row.original)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button variant="outline" size="icon" disabled>
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("components.expenses.tooltips.actionDisabled")}</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button variant="destructive" size="icon" disabled>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("components.expenses.tooltips.actionDisabled")}</p>
                </TooltipContent>
              </Tooltip>
            </>
          )}
        </div>
      ),
    },
  ]
}
